import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Autocomplete, Box, Divider, Drawer, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, ListSubheader, TextField, Typography } from "@mui/material";
import LockIcon from '@mui/icons-material/Lock';
import { Node } from '../peregrineFlowInterfaces';
import { IconNames } from '../Nodes/FlowNode/FlowNode';
import * as Icons from "@mui/icons-material";
import useStore from '../store';


export type OnClose = (e: any) => void;
export type OnSelectNode = (node: Node) => void;


type NodeProviderProps = {
    availableNodes: Array<Node>
    onClose: OnClose
    onSelectNode: OnSelectNode
    visible: boolean
    initialInputTypes?: string[]
    initialOutputTypes?: string[]
}

interface NodeGroup {
    [key: string]: Array<Node>
}

function NodeProvider({ availableNodes, onClose, visible = false, onSelectNode, initialInputTypes = [], initialOutputTypes = [] }: NodeProviderProps) {
    const [query, setQuery] = useState("");
    const [groupedNodes, setGroupedNodes] = useState<NodeGroup>({})
    const [selectedInputTypes, setSelectedInputTypes] = useState<string[]>(initialInputTypes);
    const [selectedOutputTypes, setSelectedOutputTypes] = useState<string[]>(initialOutputTypes);
    const isGuest = useStore((state) => state.isGuest);
    const setShowSignUpDialog = useStore((state) => state.setShowSignUpDialog);

    useEffect(() => {
        if (visible) {
            setSelectedInputTypes(initialInputTypes);
            setSelectedOutputTypes(initialOutputTypes);
        }
    }, [visible, initialInputTypes, initialOutputTypes]);

    const getUniqueDataTypes = (nodes: Node[], portType: 'input' | 'output'): string[] => {
        const types = new Set<string>();
        nodes.forEach(node => {
            const ports = portType === 'input' ? node.input_ports : node.output_ports;
            ports.forEach(port => {
                if (Array.isArray(port.data_type)) {
                    port.data_type.forEach(dt => types.add(dt));
                } else {
                    types.add(port.data_type);
                }
            });
        });
        return Array.from(types).sort();
    }

    const availableInputTypes = useMemo(() => getUniqueDataTypes(availableNodes, 'input'), [availableNodes]);
    const availableOutputTypes = useMemo(() => getUniqueDataTypes(availableNodes, 'output'), [availableNodes]);

    useEffect(() => {
        const grouped = {} as NodeGroup;

        availableNodes.forEach((v) => {
            if (!grouped.hasOwnProperty(v.group))
                grouped[v.group] = []

            grouped[v.group].push(v)
        });

        setGroupedNodes(grouped)

    }, [availableNodes, setGroupedNodes, query])

    const handleNodeSelect = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>, node: Node) => {
        // Check if guest user is trying to select a premium node
        if (isGuest && node.is_premium) {
            setShowSignUpDialog(true, 'premiumNode');
            onClose(event);
            return;
        }

        setQuery("")
        setSelectedInputTypes([]);
        setSelectedOutputTypes([]);
        // looks weird but this is a deep copy of an object
        onSelectNode(JSON.parse(JSON.stringify(node)));
        onClose(event)
    }, [setQuery, onClose, onSelectNode, isGuest, setShowSignUpDialog])



    const listForNode = (node: Node) => {
        const iconName = node.icon as IconNames;
        const Icon = Icons[iconName];


        const queryInDisplayName = node.display_name.toLowerCase().includes(query);
        const queryInGroup = node.group.toLowerCase().includes(query);
        const queryInDescription = node.description.toLowerCase().includes(query);

        const queryInInputPorts = node.input_ports.some(port => {
            if (Array.isArray(port.data_type)) {
                return port.data_type.some(dt => dt.toLowerCase().includes(query));
            }
            return port.data_type.toLowerCase().includes(query);
        });

        const queryInOutputPorts = node.output_ports.some(port => {
            if (Array.isArray(port.data_type)) {
                return port.data_type.some(dt => dt.toLowerCase().includes(query));
            }
            return port.data_type.toLowerCase().includes(query);
        });

        if (!queryInDisplayName && !queryInGroup && !queryInDescription && !queryInInputPorts && !queryInOutputPorts)
            return

        if (selectedInputTypes.length > 0) {
            const nodeInputTypes = new Set<string>();
            node.input_ports.forEach(p => {
                if (Array.isArray(p.data_type)) p.data_type.forEach(dt => nodeInputTypes.add(dt));
                else nodeInputTypes.add(p.data_type);
            });
            const hasMatchingInput = selectedInputTypes.some(type => nodeInputTypes.has(type));
            if (!hasMatchingInput) return;
        }

        if (selectedOutputTypes.length > 0) {
            const nodeOutputTypes = new Set<string>();
            node.output_ports.forEach(p => {
                if (Array.isArray(p.data_type)) p.data_type.forEach(dt => nodeOutputTypes.add(dt));
                else nodeOutputTypes.add(p.data_type);
            });
            const hasMatchingOutput = selectedOutputTypes.some(type => nodeOutputTypes.has(type));
            if (!hasMatchingOutput) return;
        }

        return (
            <ListItem key={node.name} alignItems="flex-start">
                <ListItemButton
                    onClick={(e) => { handleNodeSelect(e, node) }}
                    sx={{
                        opacity: isGuest && node.is_premium ? 0.6 : 1
                    }}
                >
                    <ListItemAvatar>
                        <Icon />
                    </ListItemAvatar>
                    <ListItemText
                        primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{node.display_name}</span>
                                {isGuest && node.is_premium && (
                                    <LockIcon
                                        sx={{
                                            fontSize: '16px',
                                            color: '#333333',
                                            opacity: 1
                                        }}
                                    />
                                )}
                            </Box>
                        }
                        secondary={
                            <React.Fragment>
                                {node.description}
                            </React.Fragment>
                        }
                    />
                </ListItemButton>
            </ListItem>

        )
    }

    const listForGroup = (group: string) => {
        return (
            <List key={group} subheader={
                <ListSubheader component="div" style={{ fontWeight: 'bold' }}>
                    {group.toUpperCase()}
                </ListSubheader>
            }>
                {groupedNodes[group].map(listForNode)}
                <Divider />
            </List>
        )
    }

    const drawerItems = () => (
        <Box sx={{ width: 400, padding: '10px' }}>
            <Box sx={{ padding: [4, 4, 4, 4] }}>
                <TextField autoFocus fullWidth label="Search" variant="filled" value={query} onChange={event => setQuery(event.target.value.toLocaleLowerCase())} />
                <Autocomplete
                    multiple
                    options={availableInputTypes}
                    value={selectedInputTypes}
                    onChange={(_, newValue) => setSelectedInputTypes(newValue)}
                    renderInput={(params) => (
                        <TextField {...params} variant="filled" label="Filter Input Types" placeholder="Types" />
                    )}
                    sx={{ mt: 2 }}
                />
                <Autocomplete
                    multiple
                    options={availableOutputTypes}
                    value={selectedOutputTypes}
                    onChange={(_, newValue) => setSelectedOutputTypes(newValue)}
                    renderInput={(params) => (
                        <TextField {...params} variant="filled" label="Filter Output Types" placeholder="Types" />
                    )}
                    sx={{ mt: 2 }}
                />
            </Box>

            <Typography variant='h4'>Node Overview</Typography>
            <Divider />
            <div>
                {Object.keys(groupedNodes).sort().map((group) => listForGroup(group))}
            </div>
        </Box >
    );

    return (
        <div className="NodeProvider">
            <Drawer
                open={visible}
                onClose={(e) => { setQuery(""); setSelectedInputTypes([]); setSelectedOutputTypes([]); onClose(e) }}
            >
                {drawerItems()}
            </Drawer>

        </div>
    );
}

export default NodeProvider;
